import { Suspense, lazy } from 'react'

import { ErrorBoundary } from './error-boundary'
import { useRoute } from './route'
import { Home } from './routes/home'

/*
 * `status.pushcaster.io` — the shell.
 *
 * V1_2_SPEC.md §4.1 fixes the order of what goes inside it, and §4 fixes the
 * character: one column, nothing above the first line, calmer than the
 * dashboard, and understood in a few seconds. The shell holds no data of its
 * own and therefore cannot fail — which is what keeps the header, the footer and
 * the page's frame on screen through §5.3's unavailable condition.
 *
 * ── Why the header is written here and not imported ────────────────────────
 *
 * `packages/ui`'s `ProductIdentity` is the dashboard's sidebar band, and it
 * pulls `Tooltip` — Radix, about 13 kB gzipped — for a collapsed state this page
 * does not have. §8 budgets this app at 75 kB of initial JS and S4.1 measured
 * React alone at 62.5 kB of it, so a primitive is imported here when it carries
 * behaviour and restated when it carries only appearance. Colour, type and
 * spacing still come from the tokens; nothing below names a value.
 *
 * ── The incident route is lazy ─────────────────────────────────────────────
 *
 * One of the four constraints S4.1's measurement imposed, recorded in
 * `check-bundle-size.mjs`. A reader who arrives at the home page during an
 * outage does not pay for a screen they have not asked for.
 */

const Incident = lazy(() =>
  import('./routes/incident').then((module) => ({ default: module.Incident })),
)

export function App() {
  const route = useRoute()

  return (
    <div className="flex min-h-dvh flex-col bg-bg-app text-text-primary">
      <header className="safe-block-start border-b border-border-subtle">
        <div className="safe-inline mx-auto flex w-full max-w-3xl items-baseline justify-between gap-4 px-4 py-4">
          <div>
            <p className="type-label">PushCaster</p>
            <h1 className="mt-1 text-lg font-medium text-text-emphasis">Platform status</h1>
          </div>
          <a
            href="https://pushcaster.io"
            className="rounded-xs text-xs text-text-muted underline-offset-2 hover:underline"
          >
            pushcaster.io
          </a>
        </div>
      </header>

      <main className="safe-inline mx-auto w-full max-w-3xl grow px-4 py-6">
        {/* Keyed on the route so a failure on one screen does not leave the
            other unreachable: navigating away remounts the boundary. */}
        <ErrorBoundary key={route.name === 'incident' ? route.reference : 'home'}>
          {route.name === 'incident' ? (
          /* The fallback is a line of text and not a skeleton: this app carries
             no copy of the incident's shape, so a skeleton would be inventing
             the dimensions of something it has never seen. */
            <Suspense fallback={<p className="text-sm text-text-muted">Loading incident…</p>}>
              <Incident reference={route.reference} />
            </Suspense>
          ) : (
            <Home />
          )}
        </ErrorBoundary>
      </main>

      <footer className="safe-block-end border-t border-border-subtle">
        <div className="safe-inline mx-auto w-full max-w-3xl px-4 py-4">
          <p className="text-xs text-text-muted">
            Operational status only. For account or billing help, contact support from your
            dashboard.
          </p>
        </div>
      </footer>
    </div>
  )
}
